import React, { useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import CloseIcon24 from "../../../../assets/svg/icons/common/CloseIcon24";
import Button from "../../../../components/new-button";
import useCommonStore from "../../../../stores/common";
import useTicketStore from "../../../../stores/ticket";
import TagInput from "../../../../components/form/tag-input";
import Select from "../../../../components/form/select";
import MonthPicker from "../../../../components/form/month-picker";
import { fetchTicketOptions } from "../../../../services/ticket";
import { useDbaSearch } from "../../../../hooks/general/useDbaSearch";
import {
  parse,
  startOfMonth,
  endOfMonth,
  format,
  startOfYear,
  endOfYear,
  subYears,
} from "date-fns";
import DarkCross from "../../../../assets/svg/icons/common/dark-cross";
import { useSavedFilters } from "../../../../hooks/useSavedFilters";
import SavedFiltersSection from "../../../../components/SavedFiltersSection/SavedFiltersSection";
import { isServiceTicketFilterSnapshotEmpty } from "../../../../utils/savedFilterSnapshotEmpty";
import styles from "./styles.module.scss";

const SAVED_FILTER_PAGE = "service_tickets";

function FilterDrawer() {
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);
  const filterDrawerOpen = useCommonStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const filters = useTicketStore((state) => state.ticketFilterSearchQuery);
  const { options } = useTicketStore((state) => ({
    options: state.options,
  }));
  const [optionsLoading, setOptionsLoading] = React.useState(false);
  const { dbaOptions, dbaLoading, searchDba } = useDbaSearch();
  const [tempFilters, setTempFilters] = React.useState(filters);

  useEffect(() => {
    if (filterDrawerOpen) {
      setTempFilters(filters);
    }
  }, [filterDrawerOpen]);
  const setFilters = useTicketStore(
    (state) => state.setTicketFilterSearchQuery,
  );

  const handleClear = () => {
    const clearedFilters = {
      page: 1,
      search: "",
      dba: [],
      problemType: "",
      ownerGroup: "",
      created: {
        from: "",
        to: "",
      },
      status: ["O"],
    };
    setTempFilters(clearedFilters);
    setFilters(clearedFilters);
    setFilterDrawerOpen(false);
  };

  const handleStatusChange = (selectedStatuses) => {
    setTempFilters({
      ...tempFilters,
      status: selectedStatuses.map((s) => s.value),
    });
  };

  const handleMonthPickerChange = (data) => {
    let startDate, endDate;
    const currentDate = new Date();
    let rangeType = "MTD";

    if (["MTD", "LTY", "YTD"].includes(data.month)) {
      rangeType = data.month;
      if (data.month === "YTD") {
        startDate = startOfYear(currentDate);
        endDate = currentDate;
      } else if (data.month === "MTD") {
        startDate = startOfMonth(currentDate);
        endDate = currentDate;
      } else if (data.month === "LTY") {
        const lastYearDate = subYears(currentDate, 1);
        startDate = startOfYear(lastYearDate);
        endDate = endOfYear(lastYearDate);
      }
    } else {
      rangeType = "MTD";
      const parsedDate = parse(
        `${data.month} ${data.year}`,
        "MMM yyyy",
        new Date(),
      );
      startDate = parsedDate;
      endDate = endOfMonth(parsedDate);
    }

    setTempFilters({
      ...tempFilters,
      created: {
        from: format(startDate, "yyyy-MM-dd"),
        to: format(endDate, "yyyy-MM-dd"),
      },
    });
  };

  const handleRemoveFilter = (type, value) => {
    switch (type) {
      case "dba":
        setTempFilters({
          ...tempFilters,
          dba: tempFilters.dba.filter((d) => d.value !== value),
        });
        break;
      case "problemType":
        setTempFilters({
          ...tempFilters,
          problemType: "",
        });
        break;
      case "ownerGroup":
        setTempFilters({
          ...tempFilters,
          ownerGroup: "",
        });
        break;
      case "created":
        setTempFilters({
          ...tempFilters,
          created: {
            from: "",
            to: "",
          },
        });
        break;
      case "status":
        setTempFilters({
          ...tempFilters,
          status: tempFilters.status.filter((s) => s !== value),
        });
        break;
    }
  };

  const hasActiveFilters =
    tempFilters.dba.length > 0 ||
    tempFilters.problemType ||
    tempFilters.ownerGroup ||
    tempFilters.created.from ||
    tempFilters.created.to ||
    tempFilters.status.length > 0;

  const getCurrentFilterSnapshot = () => ({
    dba: [...(tempFilters.dba || [])],
    problemType: tempFilters.problemType || "",
    ownerGroup: tempFilters.ownerGroup || "",
    created: {
      from: tempFilters.created?.from || "",
      to: tempFilters.created?.to || "",
    },
    status: [...(tempFilters.status || [])],
  });

  const applySavedFilterSnapshot = (saved) => {
    setTempFilters((prev) => ({
      ...prev,
      dba: Array.isArray(saved.dba) ? saved.dba : [],
      problemType: saved.problemType ?? "",
      ownerGroup: saved.ownerGroup ?? "",
      created: {
        from: saved.created?.from ?? "",
        to: saved.created?.to ?? "",
      },
      status: Array.isArray(saved.status) ? saved.status : [],
    }));
  };

  const getAppliedSnapshot = () => {
    const q = useTicketStore.getState().ticketFilterSearchQuery;
    return {
      dba: [...(q.dba || [])],
      problemType: q.problemType || "",
      ownerGroup: q.ownerGroup || "",
      created: {
        from: q.created?.from || "",
        to: q.created?.to || "",
      },
      status: [...(q.status || [])],
    };
  };

  const savedFiltersApi = useSavedFilters({
    page: SAVED_FILTER_PAGE,
    drawerOpen: filterDrawerOpen,
    getFilterSnapshot: getCurrentFilterSnapshot,
    applyFilterSnapshot: applySavedFilterSnapshot,
    isSnapshotEmpty: isServiceTicketFilterSnapshotEmpty,
    getAppliedSnapshot,
  });

  const handleApply = () => {
    setFilters(tempFilters);
    savedFiltersApi.commitAppliedSavedFilter();
    setFilterDrawerOpen(false);
  };

  return (
    <Drawer
      open={filterDrawerOpen}
      direction={isMobile ? "left" : "right"}
      className={styles.filter_drawer}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
      onClose={() => setFilterDrawerOpen(false)}
    >
      <div className={styles.drawer_container}>
        <div className={styles.drawer_title_block}>
          <div className={styles.head}>
            <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon24 />
            </ButtonIcon>
          </div>

          <div className={styles.header}>
            <h5>Filter</h5>
            {hasActiveFilters && (
              <div className={styles.active_filters}>
              {tempFilters.dba.map((dba) => (
                <div key={dba.value} className={styles.filter_chip}>
                  <span>{dba.label}</span>
                  <button onClick={() => handleRemoveFilter("dba", dba.value)}>
                    <DarkCross />
                  </button>
                </div>
              ))}
              {tempFilters.problemType && (
                <div className={styles.filter_chip}>
                  <span>
                    {
                      options.problem_types.find(
                        (p) => p.value === tempFilters.problemType,
                      )?.label
                    }
                  </span>
                  <button onClick={() => handleRemoveFilter("problemType")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {tempFilters.ownerGroup && (
                <div className={styles.filter_chip}>
                  <span>
                    {
                      options.owner_groups.find(
                        (r) => r.value === tempFilters.ownerGroup,
                      )?.label
                    }
                  </span>
                  <button onClick={() => handleRemoveFilter("ownerGroup")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {(tempFilters.created.from || tempFilters.created.to) && (
                <div className={styles.filter_chip}>
                  <span>
                    {tempFilters.created.from && tempFilters.created.to
                      ? `${tempFilters.created.from} - ${tempFilters.created.to}`
                      : tempFilters.created.from
                        ? `From ${tempFilters.created.from}`
                        : `To ${tempFilters.created.to}`}
                  </span>
                  <button onClick={() => handleRemoveFilter("created")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {tempFilters.status.map((status) => (
                <div key={status} className={styles.filter_chip}>
                  <span>
                    {options?.statuses?.find((s) => s.value === status)?.label}
                  </span>
                  <button onClick={() => handleRemoveFilter("status", status)}>
                    <DarkCross />
                  </button>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>

        <div className={styles.content}>
          <SavedFiltersSection
            savedFilterOptions={savedFiltersApi.savedFilterOptions}
            selectedSavedFilter={savedFiltersApi.selectedSavedFilter}
            onSavedFilterChange={savedFiltersApi.handleSavedFilterSelect}
            onDeleteSavedFilter={savedFiltersApi.handleDeleteSavedFilter}
            isDeletingFilter={savedFiltersApi.isDeletingFilter}
            editFilterName={savedFiltersApi.editFilterName}
            onEditFilterNameChange={savedFiltersApi.setEditFilterName}
            onUpdateSavedFilter={savedFiltersApi.handleUpdateSavedFilter}
            isUpdatingFilter={savedFiltersApi.isUpdatingFilter}
            showSaveFilterInput={savedFiltersApi.showSaveFilterInput}
            onShowSaveInput={savedFiltersApi.openSaveFilterInput}
            newFilterName={savedFiltersApi.newFilterName}
            onNewFilterNameChange={savedFiltersApi.setNewFilterName}
            onSaveFilter={savedFiltersApi.handleSaveFilter}
            isSavingFilter={savedFiltersApi.isSavingFilter}
            onCancelNewFilter={() => {
              savedFiltersApi.cancelSaveFilterInput();
              savedFiltersApi.setNewFilterName("");
            }}
          />

          <div className={styles.filter_section}>
            <h6 className={styles.title}>DBA</h6>
            <div className={styles.filter_body}>
              <TagInput
                id="dba-search"
                placeholder="Search"
                value={tempFilters.dba}
                options={dbaOptions}
                onChange={(selected) => {
                  setTempFilters({
                    ...tempFilters,
                    dba: selected,
                  });
                }}
                onSearchChange={searchDba}
                loading={dbaLoading}
                search
                async
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>PROBLEM TYPE</h6>
            <div className={styles.filter_body}>
              <Select
                id="problem-type"
                placeholder="Select Type"
                value={tempFilters.problemType}
                options={options?.problem_types || []}
                onChange={(value) => {
                  setTempFilters({
                    ...tempFilters,
                    problemType: value,
                  });
                }}
                loading={optionsLoading}
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>OWNER GROUP</h6>
            <div className={styles.filter_body}>
              <Select
                id="owner-group"
                placeholder="Select Owner Group"
                value={tempFilters.ownerGroup}
                options={options?.owner_groups || []}
                onChange={(value) => {
                  setTempFilters({
                    ...tempFilters,
                    ownerGroup: value,
                  });
                }}
                loading={optionsLoading}
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>CREATED</h6>
            <div className={styles.period_label}>Period</div>
            <div className={styles.filter_body}>
              <MonthPicker
                placeholder="Select Period"
                onSubmit={handleMonthPickerChange}
                changeOnSubmit
                showYearFilters
                showActions
                variant="fieldFilter"
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>STATUS</h6>
            <div className={styles.filter_body}>
              <div className={styles.status_options}>
                {options?.statuses?.map((status) => (
                  <label key={status.value} className={styles.status_option}>
                    <input
                      type="checkbox"
                      value={status.value}
                      checked={tempFilters.status.includes(status.value)}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTempFilters({
                          ...tempFilters,
                          status: e.target.checked
                            ? [...tempFilters.status, value]
                            : tempFilters.status.filter((s) => s !== value),
                        });
                      }}
                    />
                    <span className={styles.checkbox_label}>
                      {status.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" size="large" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" size="large" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

export default FilterDrawer;
